// A small 3D ferrule viewer, for places that want to show the part without
// carrying the whole Design Studio.
//
// It renders with the studio's own shaders (assets/feral-shaders.js), so a
// ferrule here looks like the same ferrule there. Three.js is fetched on first
// use rather than on page load, because most people arrive on a phone from a
// social link and should not pay for a 3D engine before they have chosen
// anything.
//
//   const v = await FERAL_VIEWER.create(el);
//   v.setModel('1in');
//   v.setColors({ surface: '#f0f0f0', feature: '#1c1c1c', gradient: false });
//   v.setTexture('https://…/texture-pollack.jpg');
//
// Not rendered here: the initials disk and the logo disk. Those are separate
// meshes the studio builds, and the walkthrough shows them in the summary
// instead. Worth porting if this viewer ever becomes the main one.

window.FERAL_VIEWER = (function () {

  var THREE_URLS = [
    'https://cdn.jsdelivr.net/npm/three@0.128.0/build/three.min.js',
    'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/controls/OrbitControls.js',
    'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/OBJLoader.js',
  ];

  var loading = null;

  function loadThree() {
    if (window.THREE && window.THREE.OBJLoader) return Promise.resolve();
    if (loading) return loading;
    loading = THREE_URLS.reduce(function (chain, url) {
      return chain.then(function () {
        return new Promise(function (res, rej) {
          var s = document.createElement('script');
          s.src = url;
          s.onload = res;
          s.onerror = function () { rej(new Error('could not load ' + url)); };
          document.head.appendChild(s);
        });
      });
    }, Promise.resolve());
    return loading;
  }

  // Ported from the studio. The two radial axes of a cylinder are nearly equal
  // and the height axis differs, so whichever axis leaves the other two most
  // alike is the height.
  // The three faces the Design Studio offers, so a build reads the same in both.
  var FONT_CSS = {
    sans:   function (n) { return '700 ' + n + 'px Arial,sans-serif'; },
    serif:  function (n) { return '700 ' + n + 'px Georgia,serif'; },
    script: function (n) { return 'italic 400 ' + n + 'px "Palatino Linotype",Palatino,serif'; },
  };

  function loadImage(url) {
    return new Promise(function (res, rej) {
      var im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = function () { res(im); };
      im.onerror = function () { rej(new Error('image failed: ' + url)); };
      im.src = url;
    });
  }

  function detectCylAxis(THREE, rawBox) {
    var s = new THREE.Vector3(); rawBox.getSize(s);
    var c = new THREE.Vector3(); rawBox.getCenter(c);
    var ratios = [
      Math.min(s.y, s.z) / Math.max(s.y, s.z),
      Math.min(s.x, s.z) / Math.max(s.x, s.z),
      Math.min(s.x, s.y) / Math.max(s.x, s.y),
    ];
    var h = ratios.indexOf(Math.max.apply(null, ratios));
    if (h === 0) return { sel: 0, cylMin: rawBox.min.x, cylRange: s.x, rA: c.y, rB: c.z };
    if (h === 1) return { sel: 1, cylMin: rawBox.min.y, cylRange: s.y, rA: c.x, rB: c.z };
    return { sel: 2, cylMin: rawBox.min.z, cylRange: s.z, rA: c.x, rB: c.y };
  }

  // The bore is the inner hole: sort every vertex by radius and the biggest gap
  // in that sorted list is the wall between hole and body.
  function computeBoreR(obj, cx, cz) {
    var radii = [];
    obj.traverse(function (c) {
      if (!c.isMesh) return;
      var pos = c.geometry.attributes.position;
      for (var i = 0; i < pos.count; i++) {
        var dx = pos.getX(i) - cx, dz = pos.getZ(i) - cz;
        radii.push(Math.sqrt(dx * dx + dz * dz));
      }
    });
    radii.sort(function (a, b) { return a - b; });
    var maxGap = 0, gapMid = radii[0] || 0;
    for (var i = 0; i < radii.length - 1; i++) {
      var gap = radii[i + 1] - radii[i];
      if (gap > maxGap) { maxGap = gap; gapMid = (radii[i] + radii[i + 1]) / 2; }
    }
    return gapMid;
  }

  /* Which way to turn the part so a feature faces the camera.

     The two features do NOT share an angle, which is easy to get wrong:

     - A LOGO is composited into the wrap texture at its centre, u = 0.5. The
       shader maps u through 1 - (atan2(db, da)/2pi + 0.5), so u = 0.5 is
       atan2 = 0, the model's +X. Camera azimuth pi/2.
     - INITIALS are a separate cylinder mesh. Three's CylinderGeometry runs
       theta from 0 at +Z, so the text at u = 0.5 of its canvas lands at -Z -
       and the band is then turned by rotation.y = pi, bringing it back to +Z.
       Camera azimuth 0, which is where the camera already sits.

     Using the logo angle for an initials build pushes the text to the
     silhouette edge, which is exactly what the first cart thumbnails showed. */
  var FACE_LOGO     = Math.PI / 2;   // +X, the middle of the wrap
  var FACE_INITIALS = 0;             // +Z, where the band's text ends up

  function create(el, opts) {
    opts = opts || {};
    // 0 off, 1 two colours, 2 three. One place so the two shader paths and
    // every update site cannot drift apart.
    function gradientLevel() {
      return Math.max(0, Math.min(2, state.gradient | 0));
    }

    var objCache = {};
    var texCache = {};
    var state = {
      model: null,
      surface: '#f0f0f0',
      feature: '#1c1c1c',
      feature2: '#c8a84b',
      feature3: '#8b2f2f',
      // 0 = off, 1 = two colours, 2 = three. Kept numeric rather than a second
      // boolean so the shader can read it directly and old saved designs, which
      // carry a plain true/false, still coerce correctly.
      gradient: 0,
      // Where the two colours hand over, as a share of the model. 0.5 is the
      // even blend this had before the control existed.
      fadeSplit: 0.5,
      texUrl: null,
      flipH: false,
      flipV: false,
      fadeSplit: 0.5,
      // Collected by the walkthrough from the start, but never drawn until now:
      // the review step said "Initials: WCH" beside a ferrule showing none.
      logoUrl: null,
      initials: '',
      initFont: 'sans',
      initColor: '#000000',
    };

    var THREE, scene, camera, renderer, controls, group, raf = null, disposed = false;
    var loadToken = 0;
    var initialsBand = null;   // wrapped text mesh, rebuilt on every change
    var capInfo = null;        // model bounds after scaling, for sizing that band
    var overlayToken = 0;      // so a slow logo cannot overwrite a newer choice

    return loadThree().then(function () {
      THREE = window.THREE;
      var SH = window.FERAL_SHADERS;

      scene = new THREE.Scene();
      group = new THREE.Group();
      scene.add(group);

      var w = el.clientWidth || 260;
      var h = el.clientHeight || 260;
      camera = new THREE.PerspectiveCamera(38, w / h, 0.1, 100);
      camera.position.set(0, 0.6, 4.2);

      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setSize(w, h, false);
      renderer.domElement.style.width = '100%';
      renderer.domElement.style.height = '100%';
      renderer.domElement.style.display = 'block';
      renderer.domElement.style.touchAction = 'pan-y';   // let the page scroll on a phone

      // A viewer that only spins takes no pointer events at all.
      if (opts.interactive === false) renderer.domElement.style.pointerEvents = 'none';
      el.appendChild(renderer.domElement);

      // Speed shared by every mode below, and matched in the Design Studio, so a
      // part turns at the same rate wherever it appears. Negative reads as left
      // to right. OrbitControls advances this much per frame assuming 60fps.
      var SPIN_SPEED = -4.0;                                   // ~15s per turn
      var SPIN_PER_FRAME = (2 * Math.PI / 60 / 60) * SPIN_SPEED;

      /* On a phone, OrbitControls is the wrong tool: it captures the pointer on
         touchstart, so a thumb swipe that happens to begin on the part is taken
         as a rotation and the page does not scroll. Dropping input altogether
         fixed the scrolling and lost the spinning, which is not the trade
         anyone wants.

         So on touch we drive the camera ourselves and arbitrate the gesture:
         the first few pixels decide. Mostly sideways is a spin and we take it;
         anything else is left alone and the page scrolls as normal. The
         decision is made once per touch, so a drag never changes its mind
         halfway. */
      var touchSpin = opts.touchSpin === true;
      var azimuth = 0, dragging = false, claimed = null;
      var startX = 0, startY = 0, lastX = 0;
      var camR = Math.sqrt(camera.position.x * camera.position.x +
                           camera.position.z * camera.position.z);
      var camY = camera.position.y;

      if (THREE.OrbitControls && !touchSpin) {
        controls = new THREE.OrbitControls(camera, renderer.domElement);
        controls.enableZoom = false;
        controls.enablePan = false;
        // OrbitControls checks `enabled` in its pointer handlers but not in
        // update(), so this stops a drag being read while the spin carries on.
        if (opts.interactive === false) controls.enabled = false;
        controls.enableDamping = true;
        controls.autoRotate = opts.autoRotate !== false;
        controls.autoRotateSpeed = SPIN_SPEED;
        controls.target.set(0, 0, 0);
      }

      if (touchSpin) {
        var cv = renderer.domElement;
        cv.style.touchAction = 'pan-y';   // the browser keeps vertical panning

        // Only the part itself is grabbable. The canvas is far wider than the
        // ferrule, so a thumb landing in the space beside it belongs to the
        // page - that is what stops anyone getting stuck here unable to reach
        // the options underneath. A raycast answers "is the finger on it?"
        // exactly, rather than guessing from a bounding box.
        var ray = new THREE.Raycaster();
        var ndc = new THREE.Vector2();
        function onPart(clientX, clientY) {
          var r = cv.getBoundingClientRect();
          if (!r.width || !r.height) return false;
          ndc.x = ((clientX - r.left) / r.width) * 2 - 1;
          ndc.y = -((clientY - r.top) / r.height) * 2 + 1;
          ray.setFromCamera(ndc, camera);
          return ray.intersectObject(group, true).length > 0;
        }

        cv.addEventListener('touchstart', function (e) {
          if (e.touches.length !== 1) { claimed = 'scroll'; return; }
          var t = e.touches[0];
          // Off the part: hand the whole gesture to the page immediately.
          claimed = onPart(t.clientX, t.clientY) ? null : 'scroll';
          dragging = false;
          startX = lastX = t.clientX;
          startY = t.clientY;
        }, { passive: true });

        cv.addEventListener('touchmove', function (e) {
          if (claimed === 'scroll' || e.touches.length !== 1) return;
          var x = e.touches[0].clientX, y = e.touches[0].clientY;
          if (!claimed) {
            var dx = Math.abs(x - startX), dy = Math.abs(y - startY);
            if (dx < 8 && dy < 8) return;          // too early to tell
            // On the part, but the model only turns about its own axis, so a
            // vertical drag has nothing to do here and is worth more to the
            // page. Sideways spins it; anything else scrolls.
            claimed = dx > dy ? 'spin' : 'scroll';
            dragging = claimed === 'spin';
            if (claimed === 'scroll') return;
          }
          azimuth -= (x - lastX) * 0.012;
          lastX = x;
          e.preventDefault();                       // ours now, so do not scroll
        }, { passive: false });

        var end = function () { dragging = false; claimed = null; };
        cv.addEventListener('touchend', end, { passive: true });
        cv.addEventListener('touchcancel', end, { passive: true });
      }

      function frame() {
        if (disposed) return;
        raf = requestAnimationFrame(frame);
        if (touchSpin) {
          // Keep turning unless a finger is on it, so letting go resumes rather
          // than stopping dead.
          if (!dragging && opts.autoRotate !== false) azimuth += SPIN_PER_FRAME;
          camera.position.set(Math.sin(azimuth) * camR, camY, Math.cos(azimuth) * camR);
          camera.lookAt(0, 0, 0);
        } else if (controls) {
          controls.update();
        }
        renderer.render(scene, camera);
      }
      frame();

      function eachFerruleMesh(fn) {
        group.traverse(function (c) { if (c.isMesh && c.material && c.material.uniforms) fn(c.material.uniforms); });
      }

      function makeKyroMat(cx, cz, boreR, featTh, yMin, yMax) {
        return new THREE.ShaderMaterial({
          vertexShader: SH.VERT, fragmentShader: SH.FRAG,
          uniforms: {
            uSurface:   { value: new THREE.Color(state.surface) },
            uFeature:   { value: new THREE.Color(state.feature) },
            uFeature2:  { value: new THREE.Color(state.feature2) },
            uFeature3:  { value: new THREE.Color(state.feature3) },
            uGradient:  { value: gradientLevel() },
            uFadeSplit: { value: state.fadeSplit },
            uLocalYMin: { value: yMin },
            uLocalYMax: { value: yMax },
            uAxisXZ:    { value: new THREE.Vector2(cx, cz) },
            uRawBoreR:  { value: boreR },
            uFeatTh:    { value: featTh },
            uTex:       { value: null },
            uHasTex:    { value: false },
            uFlipH:     { value: false },
            uFlipV:     { value: false },
          },
          side: THREE.DoubleSide,
        });
      }

      function makeStdMat(cylInfo, modelKey) {
        return new THREE.ShaderMaterial({
          vertexShader: SH.STD_VERT, fragmentShader: SH.STD_FRAG,
          extensions: { derivatives: true },
          uniforms: {
            uTex:      { value: null },
            uHasTex:   { value: false },
            uFlipH:    { value: false },
            uFlipV:    { value: false },
            uColor:    { value: new THREE.Color(state.surface) },
            uColor2:   { value: new THREE.Color(state.feature2) },
            uColor3:   { value: new THREE.Color(state.feature3) },
            uGradient: { value: gradientLevel() },
            uFadeSplit: { value: state.fadeSplit },
            uCylAxis:  { value: cylInfo.sel },
            uCylMin:   { value: cylInfo.cylMin },
            uCylRange: { value: cylInfo.cylRange },
            uRadA:     { value: cylInfo.rA },
            uRadB:     { value: cylInfo.rB },
            uEndCapTh: { value: modelKey === 'shorty' ? 0.88 : 0.70 },
          },
          side: THREE.FrontSide,
        });
      }

      function clearGroup() {
        group.children.slice().forEach(function (c) {
          group.remove(c);
          c.traverse && c.traverse(function (n) {
            if (n.geometry) n.geometry.dispose();
            if (n.material) n.material.dispose();
          });
        });
      }

      function applyObj(obj, modelKey) {
        var info = (window.FERAL_CATALOG && window.FERAL_CATALOG.MODEL_INFO[modelKey]) || {};
        var isKyro = !!info.isKyro;
        var rawBox = new THREE.Box3().setFromObject(obj);
        var rawSize = new THREE.Vector3(); rawBox.getSize(rawSize);
        var rawCenter = new THREE.Vector3(); rawBox.getCenter(rawCenter);
        var boreR = computeBoreR(obj, rawCenter.x, rawCenter.z);
        var scale = 2.0 / Math.max(rawSize.x, rawSize.y, rawSize.z);
        var featTh = SH.MODEL_FEAT_TH[modelKey];
        if (featTh == null) featTh = 0.95;
        var cylInfo = isKyro ? null : detectCylAxis(THREE, rawBox);

        obj.scale.setScalar(scale);
        obj.position.sub(rawCenter.clone().multiplyScalar(scale));

        obj.traverse(function (c) {
          if (!c.isMesh) return;
          if (!c.geometry.attributes.normal) c.geometry.computeVertexNormals();
          c.material = isKyro
            ? makeKyroMat(rawCenter.x, rawCenter.z, boreR, featTh, rawBox.min.y, rawBox.max.y)
            : makeStdMat(cylInfo, modelKey);
        });

        clearGroup();
        group.add(obj);

        // Measure the model as it ends up on screen, not as it left CAD - every
        // model is scaled and recentred above, by different amounts.
        var wb = new THREE.Box3().setFromObject(obj);
        var ws = new THREE.Vector3(); wb.getSize(ws);
        capInfo = {
          topY: wb.max.y, bottomY: wb.min.y,
          maxR: Math.max(ws.x, ws.z) / 2,
          takesInitials: !isKyro,
          obj: obj,
        };

        pushColors();
        pushTexture();
        rebuildInitials();
      }

      /* Initials, as a transparent band wrapped round the ferrule.
       *
       * The canvas has to match the band's real circumference-to-height ratio,
       * or one fixed-aspect texture stretches differently on every model - which
       * is what distorted initials on the Shorty in the Studio. */
      /* Widest point of the model within a horizontal slice.
       *
       * A ferrule tapers, so a band built at the model's overall max radius
       * stands off the surface everywhere above the base - the text then draws
       * outside the silhouette and reads as floating. Sampling the real
       * geometry at the band's own height keeps it on the part. */
      function radiusAt(obj, y, halfSpan) {
        var best = 0, v = new THREE.Vector3();
        obj.traverse(function (c) {
          if (!c.isMesh || !c.geometry || !c.geometry.attributes.position) return;
          var pos = c.geometry.attributes.position;
          for (var i = 0; i < pos.count; i++) {
            v.fromBufferAttribute(pos, i);
            c.localToWorld(v);
            if (Math.abs(v.y - y) > halfSpan) continue;
            var r = Math.sqrt(v.x * v.x + v.z * v.z);
            if (r > best) best = r;
          }
        });
        return best;
      }

      function rebuildInitials() {
        if (initialsBand) {
          group.remove(initialsBand);
          initialsBand.geometry.dispose();
          if (initialsBand.material.map) initialsBand.material.map.dispose();
          initialsBand.material.dispose();
          initialsBand = null;
        }
        var text = (state.initials || '').trim().toUpperCase().slice(0, 3);
        if (!text || !capInfo || !capInfo.takesInitials) return;

        var totalH = capInfo.topY - capInfo.bottomY;
        var bandH = totalH * 0.46;
        var centerY = capInfo.bottomY + totalH * 0.5;
        var slice = bandH * 0.18;
        // Follow the taper: wider at the bottom of the band than the top.
        var rBot = radiusAt(capInfo.obj, centerY - bandH / 2, slice) || capInfo.maxR;
        var rTop = radiusAt(capInfo.obj, centerY + bandH / 2, slice) || rBot;
        rBot *= 0.995; rTop *= 0.995;
        var r = (rBot + rTop) / 2;
        var circ = 2 * Math.PI * r;
        var W = 1024;
        var H = (circ > 0 && bandH > 0)
          ? Math.max(96, Math.min(Math.round(W / (circ / bandH)), 1024)) : 256;

        var cv = document.createElement('canvas');
        cv.width = W; cv.height = H;
        var ctx = cv.getContext('2d');
        ctx.clearRect(0, 0, W, H);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.shadowColor = 'rgba(0,0,0,0.45)';
        ctx.shadowBlur = 10;
        ctx.fillStyle = state.initColor || '#000000';
        var fontFor = FONT_CSS[state.initFont] || FONT_CSS.sans;
        var size = H * 0.7;
        ctx.font = fontFor(size);
        var measured = ctx.measureText(text).width;
        // 0.55 of the circumference, so the text sits on the face the viewer
        // is looking at rather than wrapping round out of sight.
        if (measured > 0) size = Math.min(size, size * (W * 0.55) / measured);
        ctx.font = fontFor(Math.floor(size));
        ctx.fillText(text, W / 2, H / 2);

        var tex = new THREE.CanvasTexture(cv);
        tex.encoding = THREE.sRGBEncoding;
        initialsBand = new THREE.Mesh(
          new THREE.CylinderGeometry(rTop, rBot, bandH, 64, 1, true),
          new THREE.MeshBasicMaterial({ map: tex, transparent: true,
            depthWrite: false, depthTest: false, side: THREE.FrontSide }));
        initialsBand.rotation.y = Math.PI;
        initialsBand.position.y = centerY;
        group.add(initialsBand);
      }

      /* A logo is composited into the surface texture rather than floated on a
       * second mesh, which is how the Studio does it - that way it sits under
       * the same UV mapping as a design and cannot drift out of register. */
      function pushOverlayTexture() {
        var token = ++overlayToken;
        if (!state.logoUrl) { pushTexture(); return; }
        var jobs = [loadImage(state.logoUrl)];
        if (state.texUrl) jobs.push(loadImage(state.texUrl).catch(function () { return null; }));
        Promise.all(jobs).then(function (imgs) {
          if (token !== overlayToken) return;      // a newer choice already won
          var logo = imgs[0], base = imgs[1] || null;
          var W = 2048, H = 1024;
          var cv = document.createElement('canvas');
          cv.width = W; cv.height = H;
          var ctx = cv.getContext('2d');
          if (base) ctx.drawImage(base, 0, 0, W, H);
          else { ctx.fillStyle = state.surface; ctx.fillRect(0, 0, W, H); }
          // Fit the logo into the middle third, keeping its aspect.
          var maxW = W * 0.3, maxH = H * 0.5;
          var sc = Math.min(maxW / logo.width, maxH / logo.height);
          var lw = logo.width * sc, lh = logo.height * sc;
          ctx.drawImage(logo, (W - lw) / 2, (H - lh) / 2, lw, lh);
          var tex = new THREE.CanvasTexture(cv);
          tex.encoding = THREE.sRGBEncoding;
          tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
          tex.minFilter = THREE.LinearFilter;
          tex.generateMipmaps = false;
          eachFerruleMesh(function (u) {
            if (u.uHasTex === undefined) return;
            u.uTex.value = tex;
            u.uHasTex.value = true;
            if (u.uFlipH) { u.uFlipH.value = state.flipH; u.uFlipV.value = state.flipV; }
          });
        }).catch(function () { pushTexture(); });
      }

      function pushColors() {
        eachFerruleMesh(function (u) {
          if (u.uSurface) {
            u.uSurface.value.set(state.surface);
            u.uFeature.value.set(state.feature);
            u.uFeature2.value.set(state.feature2);
            if (u.uFeature3) u.uFeature3.value.set(state.feature3);
            u.uGradient.value = gradientLevel();
          } else if (u.uColor) {
            u.uColor.value.set(state.surface);
            u.uColor2.value.set(state.feature2);
            if (u.uColor3) u.uColor3.value.set(state.feature3);
            u.uGradient.value = gradientLevel();
          }
          if (u.uFadeSplit) u.uFadeSplit.value = state.fadeSplit;
        });
      }

      function pushTexture() {
        if (!state.texUrl) {
          eachFerruleMesh(function (u) { if (u.uHasTex) { u.uHasTex.value = false; u.uTex.value = null; } });
          return;
        }
        var url = state.texUrl;
        var apply = function (tex) {
          if (state.texUrl !== url) return;   // a newer choice already won
          eachFerruleMesh(function (u) {
            if (u.uHasTex === undefined) return;
            u.uTex.value = tex;
            u.uHasTex.value = true;
            if (u.uFlipH) { u.uFlipH.value = state.flipH; u.uFlipV.value = state.flipV; }
          });
        };
        if (texCache[url]) { apply(texCache[url]); return; }
        var loader = new THREE.TextureLoader();
        loader.setCrossOrigin('anonymous');
        loader.load(url, function (tex) {
          tex.encoding = THREE.sRGBEncoding;
          tex.wrapS = THREE.ClampToEdgeWrapping;
          tex.wrapT = THREE.ClampToEdgeWrapping;
          tex.minFilter = THREE.LinearFilter;
          tex.generateMipmaps = false;
          texCache[url] = tex;
          apply(tex);
        }, undefined, function () { /* a missing texture just leaves the solid colour */ });
      }

      var api = {
        setModel: function (modelKey) {
          if (state.model === modelKey) return Promise.resolve();
          state.model = modelKey;
          var url = (window.KYRO_MODEL_URLS || {})[modelKey];
          if (!url) return Promise.resolve();
          var token = ++loadToken;

          if (objCache[modelKey]) { applyObj(objCache[modelKey].clone(), modelKey); return Promise.resolve(); }

          el.classList.add('is-loading');
          return new Promise(function (resolve) {
            new THREE.OBJLoader().load(url, function (obj) {
              // Standard models come out of CAD lying on whichever axis the
              // exporter felt like; stand them up before anything measures them.
              var info = (window.FERAL_CATALOG && window.FERAL_CATALOG.MODEL_INFO[modelKey]) || {};
              if (!info.isKyro) {
                var box = new THREE.Box3().setFromObject(obj);
                var size = new THREE.Vector3(); box.getSize(size);
                var rot = null;
                if (modelKey === 'shorty' || (size.x >= size.y && size.x >= size.z)) {
                  rot = new THREE.Matrix4().makeRotationZ(-Math.PI / 2);
                } else if (size.z >= size.x && size.z >= size.y) {
                  rot = new THREE.Matrix4().makeRotationX(-Math.PI / 2);
                }
                if (rot) obj.traverse(function (c) { if (c.isMesh) c.geometry.applyMatrix4(rot); });
                obj.traverse(function (c) { if (c.isMesh) c.geometry.computeVertexNormals(); });
              }
              objCache[modelKey] = obj.clone();
              el.classList.remove('is-loading');
              if (token === loadToken) applyObj(obj, modelKey);
              resolve();
            }, undefined, function () { el.classList.remove('is-loading'); resolve(); });
          });
        },

        setColors: function (c) {
          if (c.surface) state.surface = c.surface;
          if (c.feature) state.feature = c.feature;
          if (c.feature2) state.feature2 = c.feature2;
          if (c.feature3) state.feature3 = c.feature3;
          // Accepts the old boolean and the new 0/1/2. A saved design from
          // before three colours existed carries true and must stay two-colour.
          state.gradient = (c.gradient === true) ? 1
            : (c.gradient === false || c.gradient == null) ? 0
            : Math.max(0, Math.min(2, parseInt(c.gradient, 10) || 0));
          if (typeof c.fadeSplit === 'number') {
            state.fadeSplit = Math.min(0.95, Math.max(0.05, c.fadeSplit));
          }
          pushColors();
        },

        setTexture: function (url, flip) {
          state.texUrl = url || null;
          if (flip) { state.flipH = !!flip.flipH; state.flipV = !!flip.flipV; }
          if (state.logoUrl) pushOverlayTexture(); else pushTexture();
        },

        /* text is up to three characters; font is sans | serif | script. */
        setInitials: function (o) {
          o = o || {};
          state.initials = o.text || '';
          if (o.font) state.initFont = o.font;
          if (o.color) state.initColor = o.color;
          rebuildInitials();
        },

        setLogo: function (url) {
          state.logoUrl = url || null;
          if (state.logoUrl) pushOverlayTexture(); else pushTexture();
        },

        /* A still with the logo or initials facing the camera, for the order.

           Both are composited at the middle of the wrap - the logo at the centre
           of the overlay canvas, the initials text at W/2 - so both sit at
           u = 0.5. The shader maps u through
             u = 1 - (atan2(db, da) / 2pi + 0.5)
           so u = 0.5 is atan2 = 0, which is the model's +X. Put the camera there
           and the feature is dead centre. It also hides the wrap join, which
           lives at u = 0/1 on the opposite side.

           The camera is moved, read and put back inside one turn, so nothing is
           ever painted with the part swung round and the customer sees no jump. */
        captureFront: function (mime, quality) {
          try {
            var saved = camera.position.clone();
            var wasAuto = controls ? controls.autoRotate : false;
            if (controls) controls.autoRotate = false;

            // A logo wins: it is the bigger mark, and a build with both puts the
            // logo on the wrap and the initials on a band lower down.
            var az = state.logoUrl ? FACE_LOGO
                   : ((state.initials || '').trim() ? FACE_INITIALS : FACE_LOGO);
            var r = Math.sqrt(camera.position.x * camera.position.x +
                              camera.position.z * camera.position.z);
            camera.position.set(r * Math.sin(az), camera.position.y, r * Math.cos(az));
            camera.lookAt(0, 0, 0);
            renderer.render(scene, camera);
            var url = renderer.domElement.toDataURL(mime || 'image/png', quality);

            camera.position.copy(saved);
            camera.lookAt(0, 0, 0);
            if (controls) { controls.autoRotate = wasAuto; controls.update(); }
            renderer.render(scene, camera);
            return url;
          } catch (e) {
            return null;
          }
        },

        /* A still of exactly what is on screen, for the order.

           The render and the read have to happen in the same turn. This
           renderer does not set preserveDrawingBuffer, so the drawing buffer is
           cleared once the frame is composited, and a read on any later tick
           comes back blank however good the picture looked. */
        capture: function (mime, quality) {
          try {
            renderer.render(scene, camera);
            return renderer.domElement.toDataURL(mime || 'image/png', quality);
          } catch (e) {
            return null;
          }
        },

        resize: function () {
          var w = el.clientWidth, h = el.clientHeight;
          if (!w || !h) return;
          camera.aspect = w / h;
          camera.updateProjectionMatrix();
          renderer.setSize(w, h, false);
        },

        dispose: function () {
          disposed = true;
          if (raf) cancelAnimationFrame(raf);
          clearGroup();
          renderer.dispose();
          // renderer.dispose() frees Three's own objects but leaves the WebGL
          // context alive, and a browser allows only a small number of those at
          // once. Every viewer built and thrown away - the home teaser, the
          // walkthrough, anything made for a capture - kept its slot until the
          // tab closed, and once the pool ran dry the next create() failed with
          // "Error creating WebGL context". Hand the slot back.
          try {
            var lose = renderer.getContext().getExtension('WEBGL_lose_context');
            if (lose) lose.loseContext();
          } catch (e) {}
          if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
        },
      };

      if (window.ResizeObserver) new ResizeObserver(function () { api.resize(); }).observe(el);
      return api;
    });
  }

  return { create: create };

})();
